import { AfterViewInit, Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Product, ProductGroup, ProductPriceLog } from '@infrab4a/connect';
import { NgxPermissionsService } from 'ngx-permissions';
import { MessageService } from 'primeng/api';
import { PageableFilter } from 'src/app/admin-api';
import { TableColumn } from 'src/app/components/table';
import { ShopProductGroupService } from 'src/app/connect-api/api/shop/shop-product-group.service';
import { ShopProductPriceLogService } from 'src/app/connect-api/api/shop/shop-product-price-log.service';
import { Role, roleAsObject } from 'src/app/models';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-shop-group-detail',
  templateUrl: './shop-group-detail.component.html',
  styleUrl: './shop-group-detail.component.scss'
})
export class ShopGroupDetailComponent implements AfterViewInit, OnInit {
  @Input() groupId?: string;
  group: Partial<ProductGroup>;
  groupProducts: Array<Product>;
  priceLogs: Array<any>;
  showDocumentModal = false;
  selectedDocument: any;
  logActionButtons = [
    {
      label: 'Ver',
      icon: 'pi pi-eye',
      action: 'viewDocument',
      display: () => true
    }
  ];
  priceLogCols = [
    new TableColumn.Builder()
      .setHeader('EAN')
      .setField('productEAN')
      .setRouterLink(
        (product: any) =>
          `/shop-products/catalog/product/${product.productId ? product.productId : product.id}`
      )
      .setType('text')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Nome')
      .setField('productName')
      .setRouterLink(
        (product: any) =>
          `/shop-products/catalog/product/${product.productId ? product.productId : product.id}`
      )
      .setType('text')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('createdAt')
      .setType('date')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Preço de custo')
      .setField('productCostPrice')
      .setType('currency')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Preço mínimo viável')
      .setField('minViablePrice')
      .setType('currency')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Target Price')
      .setField('targetPrice')
      .setType('currency')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder().setHeader('Log').setType('button').build()
  ];

  fixedPriceLogFilters: Array<PageableFilter> = [];

  productCols = [
    new TableColumn.Builder()
      .setHeader('EAN')
      .setField('EAN')
      .setRouterLink(
        (product: any) =>
          `/shop-products/catalog/product/${product.productId ? product.productId : product.id}`
      )
      .setType('text')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Nome')
      .setField('name')
      .setRouterLink(
        (product: any) =>
          `/shop-products/catalog/product/${product.productId ? product.productId : product.id}`
      )
      .setType('text')
      .setCondition('contains')
      .build(),
    new TableColumn(
      'Marca',
      'brand',
      true,
      'text',
      undefined,
      undefined,
      true,
      'contains'
    ),
    new TableColumn.Builder()
      .setHeader('Preço assinante')
      .setField('price.subscriberPrice')
      .setType('currency')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Preço')
      .setField('price.price')
      .setType('currency')
      .setCondition('equals')
      .build()
  ];

  public groupForm = new FormGroup({
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    description: new FormControl<string | undefined>(undefined),
    markupMin: new FormControl<number>(null, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(0.1)]
    }),
    markupWithoutCompetitor: new FormControl<number>(null, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(0.1)]
    }),
    discountCompetitor: new FormControl<number>(null, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(0.1)]
    }),
    subscriberDiscountPercentage: new FormControl<number>(null, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(0.1)]
    }),
    dynamicPrecification: new FormControl<boolean>(false)
  });

  private title = inject(Title);
  protected shopProductGroupService = inject(ShopProductGroupService);
  protected shopProductPriceLogService = inject(ShopProductPriceLogService);
  private permissionsService = inject(NgxPermissionsService);
  private messageService = inject(MessageService);
  permission: boolean = true;

  async ngOnInit(): Promise<void> {
    this.permission = await this.permissionsService.hasPermission([
      roleAsObject(Role.Full_Administrator).enumValue,
      roleAsObject(Role.Portfolio).enumValue
    ]);
  }

  async ngAfterViewInit(): Promise<void> {
    LoaderService.showLoader();
    this.group = await this.shopProductGroupService.getProductGroupById(
      this.groupId!
    );
    this.fixedPriceLogFilters = [
      { field: 'groupId', value: this.groupId, condition: 'equals' }
    ];
    this.patchFormValue(this.group);
    this.title.setTitle(`Loja - Grupo de Produtos - ${this.group.name}`);
    LoaderService.showLoader(false);
    this.loadGroupProducts();
  }

  patchFormValue(group: Partial<ProductGroup>) {
    this.groupForm.patchValue(group);
  }

  async submit() {
    if (!this.groupForm.valid) {
      this.groupForm.markAllAsTouched();
      this.groupForm.markAsDirty();
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Verifique os campos'
      });
      return;
    }

    try {
      LoaderService.showLoader(true);

      await this.shopProductGroupService.update(
        this.groupId,
        this.groupForm.getRawValue()
      );

      LoaderService.showLoader(false);

      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Grupo atualizado com sucesso'
      });
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Erro ao atualizar o grupo'
      });
    }
  }

  async loadGroupProducts(): Promise<void> {
    this.groupProducts =
      await this.shopProductGroupService.getAllProductsFromGroup(this.groupId!);
  }

  invalidControl(control: string): boolean {
    return (
      this.groupForm.controls[control].invalid &&
      (this.groupForm.controls[control].dirty ||
        this.groupForm.controls[control].touched)
    );
  }

  showOrderDocumentModal(event: any) {
    this.selectedDocument = event.item as ProductPriceLog;
    this.showDocumentModal = true;
  }
}
